import { useState, useEffect, createContext, useContext } from 'react';
import { X, ChevronRight, ChevronLeft, HelpCircle, Lightbulb, Search, Phone, Star, FileText, MapPin } from 'lucide-react';
import { createPortal } from 'react-dom';

// WelcomeModal: show ONLY on the homepage and a few discovery surfaces.
// (Inverted from the previous suppress-list approach because legacy garage
//  URLs without the /garage/ prefix — like /episkeyes-grylon-kleidarion-nikaia
//  — slipped through the suppress filter and triggered the modal on
//  high-intent SEO landings, causing rage-click conversion loss.)
const WELCOME_MODAL_ALLOW_PATHS = new Set([
  '/',
  '/en',
  '/en/',
]);
function shouldShowWelcomeOnPath(path) {
  return WELCOME_MODAL_ALLOW_PATHS.has(path);
}

// Context for tooltip visibility
const TooltipContext = createContext();

export function TooltipProvider({ children }) {
  const [showTooltips, setShowTooltips] = useState(true);
  const [dismissedTooltips, setDismissedTooltips] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('vroum_dismissed_tooltips') || '[]');
    } catch {
      return [];
    }
  });

  const dismissTooltip = (id) => {
    const newDismissed = [...dismissedTooltips, id];
    setDismissedTooltips(newDismissed);
    localStorage.setItem('vroum_dismissed_tooltips', JSON.stringify(newDismissed));
  };

  const isTooltipDismissed = (id) => dismissedTooltips.includes(id);
  
  const resetTooltips = () => {
    setDismissedTooltips([]);
    localStorage.removeItem('vroum_dismissed_tooltips');
  };

  return (
    <TooltipContext.Provider value={{ showTooltips, setShowTooltips, dismissTooltip, isTooltipDismissed, resetTooltips }}>
      {children}
    </TooltipContext.Provider>
  );
}

export const useTooltips = () => useContext(TooltipContext);

/**
 * InfoTooltip - Small info icon with hover/click tooltip
 */
export function InfoTooltip({ 
  id,
  content, 
  title,
  position = 'top',
  icon: Icon = HelpCircle,
  iconClassName = 'w-4 h-4 text-gray-400 hover:text-[#ff3b00] cursor-help'
}) {
  const [isVisible, setIsVisible] = useState(false);
  const { isTooltipDismissed, dismissTooltip } = useTooltips() || {};

  if (id && isTooltipDismissed?.(id)) return null;

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-gray-800',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-800',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-gray-800',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-gray-800'
  };

  return (
    <div className="relative inline-flex items-center">
      <button
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
        className="focus:outline-none"
        aria-label="Πληροφορίες"
      >
        <Icon className={iconClassName} />
      </button>
      
      {isVisible && (
        <div 
          className={`absolute z-50 ${positionClasses[position]} w-64 animate-in fade-in-0 zoom-in-95 duration-200`}
          onMouseEnter={() => setIsVisible(true)}
          onMouseLeave={() => setIsVisible(false)}
        >
          <div className="bg-gray-800 text-white rounded-lg shadow-xl p-3">
            {title && <div className="font-semibold text-sm mb-1">{title}</div>}
            <div className="text-sm text-gray-200">{content}</div>
            {id && (
              <button
                onClick={(e) => { e.stopPropagation(); dismissTooltip(id); setIsVisible(false); }}
                className="text-xs text-gray-400 hover:text-white mt-2 underline"
              >
                Μην το εμφανίζεις ξανά
              </button>
            )}
          </div>
          {/* Arrow */}
          <div className={`absolute w-0 h-0 border-8 border-transparent ${arrowClasses[position]}`} />
        </div>
      )}
    </div>
  );
}

/**
 * FeatureHighlight - Spotlight a feature with pulsing indicator
 */
export function FeatureHighlight({ 
  id,
  children, 
  title, 
  description,
  position = 'bottom',
  showOnce = true 
}) {
  const [isVisible, setIsVisible] = useState(false);
  const { isTooltipDismissed, dismissTooltip } = useTooltips() || {};
  
  useEffect(() => {
    if (showOnce && id && isTooltipDismissed?.(id)) {
      setIsVisible(false);
      return;
    }
    // Show after a short delay
    const timer = setTimeout(() => setIsVisible(true), 1000);
    return () => clearTimeout(timer);
  }, [id, showOnce, isTooltipDismissed]);

  const handleDismiss = () => {
    setIsVisible(false);
    if (id) dismissTooltip(id);
  };

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-3',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-3',
    left: 'right-full top-1/2 -translate-y-1/2 mr-3',
    right: 'left-full top-1/2 -translate-y-1/2 ml-3'
  };

  return (
    <div className="relative inline-block">
      {children}
      
      {/* Pulsing indicator */}
      {isVisible && (
        <div className="absolute -top-1 -right-1 z-40">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ff3b00] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#ff3b00]"></span>
          </span>
        </div>
      )}
      
      {/* Tooltip */}
      {isVisible && (
        <div className={`absolute z-50 ${positionClasses[position]} w-72`}>
          <div className="bg-white rounded-xl shadow-2xl border-2 border-[#ff3b00]/20 p-4 animate-in slide-in-from-bottom-2">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#ff3b00]/10 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-5 h-5 text-[#ff3b00]" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-gray-900 mb-1">{title}</h4>
                <p className="text-sm text-gray-600">{description}</p>
              </div>
              <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handleDismiss}
              className="mt-3 w-full py-2 bg-[#ff3b00] hover:bg-[#e63500] text-white rounded-lg text-sm font-medium transition-colors"
            >
              Κατάλαβα!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * WelcomeModal - First-time visitor welcome screen
 */
export function WelcomeModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Show ONLY on whitelisted paths — see WELCOME_MODAL_ALLOW_PATHS.
    const path = window.location.pathname;
    if (!shouldShowWelcomeOnPath(path)) return;

    // Check if user has seen welcome modal
    try {
      const hasSeenWelcome = localStorage.getItem('vroum_welcome_seen');
      if (!hasSeenWelcome) {
        // Delay showing modal — only on homepage/discovery pages
        const timer = setTimeout(() => setIsOpen(true), 2000);
        return () => clearTimeout(timer);
      }
    } catch { /* private browsing — skip silently */ }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    try { localStorage.setItem('vroum_welcome_seen', 'true'); } catch { /* private browsing */ }
  };

  // Wave-rage-fix: "Ας ξεκινήσουμε!" CTA should ACTUALLY do something —
  // close the modal AND send the user to the search page. Previously it
  // just closed silently → users tapped 3x thinking it was broken.
  const handleStartSearching = () => {
    handleClose();
    // Use plain navigation (no react-router import in this file's deps)
    window.location.href = '/search';
  };

  const steps = [
    {
      icon: Search,
      title: 'Βρες Συνεργείο',
      description: 'Αναζήτησε με βάση την τοποθεσία, την υπηρεσία ή τη μάρκα του αυτοκινήτου σου.',
      color: 'blue'
    },
    {
      icon: Star,
      title: 'Δες Κριτικές',
      description: 'Διάβασε αξιολογήσεις από πραγματικούς πελάτες για να βρεις το καλύτερο συνεργείο.',
      color: 'yellow'
    },
    {
      icon: FileText,
      title: 'Ζήτα Προσφορά',
      description: 'Λάβε δωρεάν προσφορές από πολλά συνεργεία και σύγκρινε τιμές.',
      color: 'green'
    },
    {
      icon: Phone,
      title: 'Κλείσε Ραντεβού',
      description: 'Επικοινώνησε απευθείας ή κλείσε online το ραντεβού σου.',
      color: 'orange'
    }
  ];

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#ff3b00] to-[#ff6b35] p-6 text-white relative">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold mb-2">Καλώς ήρθες στο VROUM!</h2>
          <p className="text-white/90 text-sm">Η #1 πλατφόρμα εύρεσης συνεργείων στην Ελλάδα</p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Current Step */}
          <div className="mb-6">
            {steps.map((s, index) => {
              const Icon = s.icon;
              const colorClasses = {
                blue: 'bg-blue-100 text-blue-600',
                yellow: 'bg-yellow-100 text-yellow-600',
                green: 'bg-green-100 text-green-600',
                orange: 'bg-orange-100 text-orange-600'
              };
              
              return (
                <div
                  key={index}
                  className={`flex items-start gap-4 p-4 rounded-xl transition-all duration-300 ${
                    index === step ? 'bg-gray-50 scale-[1.02]' : 'opacity-50'
                  }`}
                  style={{ display: index === step ? 'flex' : 'none' }}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${colorClasses[s.color]}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-1">{s.title}</h3>
                    <p className="text-gray-600">{s.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Step indicators */}
          <div className="flex justify-center gap-2 mb-6">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setStep(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === step ? 'bg-[#ff3b00] w-8' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex gap-3">
            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="flex-1 py-3 border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Πίσω
              </button>
            )}
            
            {step < steps.length - 1 ? (
              <button
                onClick={() => setStep(step + 1)}
                className="flex-1 py-3 bg-[#ff3b00] hover:bg-[#e63500] text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
              >
                Επόμενο
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleStartSearching}
                className="flex-1 py-3 bg-[#ff3b00] hover:bg-[#e63500] text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
              >
                Βρες συνεργείο
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Skip */}
          <button
            onClick={handleClose}
            className="w-full mt-3 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Παράλειψη
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * OnboardingTour - Step-by-step guided tour
 */
export function OnboardingTour({ steps, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    // Check if tour was completed
    const tourCompleted = localStorage.getItem('vroum_tour_completed');
    if (!tourCompleted) {
      const timer = setTimeout(() => setIsActive(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (!isActive || !steps[currentStep]) return;
    
    const target = document.querySelector(steps[currentStep].target);
    if (target) {
      const rect = target.getBoundingClientRect();
      setTargetRect(rect);
      
      // Scroll to element
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentStep, isActive, steps]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = () => {
    setIsActive(false);
    localStorage.setItem('vroum_tour_completed', 'true');
    onComplete?.();
  };

  if (!isActive || !steps.length) return null;

  const step = steps[currentStep];
  const Icon = step.icon || Lightbulb;

  return createPortal(
    <>
      {/* Backdrop with spotlight */}
      <div className="fixed inset-0 z-[90] pointer-events-none">
        <svg className="w-full h-full">
          <defs>
            <mask id="spotlight-mask">
              <rect width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left - 8}
                  y={targetRect.top - 8}
                  width={targetRect.width + 16}
                  height={targetRect.height + 16}
                  rx="12"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(0,0,0,0.75)"
            mask="url(#spotlight-mask)"
          />
        </svg>
      </div>

      {/* Tooltip */}
      {targetRect && (
        <div
          className="fixed z-[95] w-80 animate-in slide-in-from-bottom-4"
          style={{
            top: targetRect.bottom + 16,
            left: Math.max(16, Math.min(targetRect.left, window.innerWidth - 336))
          }}
        >
          <div className="bg-white rounded-xl shadow-2xl p-5 border-2 border-[#ff3b00]">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#ff3b00] flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-lg text-gray-900">{step.title}</h4>
                <p className="text-sm text-gray-600 mt-1">{step.content}</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {currentStep + 1} / {steps.length}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleComplete}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Παράλειψη
                </button>
                <button
                  onClick={handleNext}
                  className="px-4 py-2 text-sm bg-[#ff3b00] hover:bg-[#e63500] text-white rounded-lg font-medium transition-colors"
                >
                  {currentStep < steps.length - 1 ? 'Επόμενο' : 'Τέλος'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}

/**
 * QuickTip - Inline tip component for explanations
 */
export function QuickTip({ children, className = '' }) {
  return (
    <div className={`flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm ${className}`}>
      <Lightbulb className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
      <div className="text-blue-800">{children}</div>
    </div>
  );
}

/**
 * HelpButton - Floating help button to reshow tooltips
 */
export function HelpButton() {
  const [isOpen, setIsOpen] = useState(false);
  const { resetTooltips } = useTooltips() || {};

  const handleResetTour = () => {
    localStorage.removeItem('vroum_welcome_seen');
    localStorage.removeItem('vroum_tour_completed');
    resetTooltips?.();
    window.location.reload();
  };

  return (
    <div className="fixed bottom-24 left-4 z-40">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:shadow-xl transition-shadow border border-gray-200"
        aria-label="Βοήθεια"
      >
        <HelpCircle className="w-6 h-6 text-gray-600" />
      </button>
      
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-64 bg-white rounded-xl shadow-xl border p-4 animate-in slide-in-from-bottom-2">
          <h4 className="font-bold text-gray-900 mb-2">Χρειάζεσαι βοήθεια;</h4>
          <div className="space-y-2">
            <button
              onClick={handleResetTour}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-lg transition-colors"
            >
              Δες ξανά τον οδηγό
            </button>
            <a
              href="/voitheia"
              className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-lg transition-colors"
            >
              Βοήθεια SOS
            </a>
            <a
              href="mailto:info@vroum.gr"
              className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-lg transition-colors"
            >
              Επικοινωνία
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
